import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout from './LegalLayout.jsx';

const Mail = () => (
  <a href="mailto:ceo@civixcrm.ai">ceo@civixcrm.ai</a>
);

export default function SmsPolicy() {
  return (
    <LegalLayout title="SMS Policy — CIVIX">
      <h1>SMS Policy</h1>
      <p className="legal-meta">
        <strong>Effective date:</strong> May 13, 2026<br />
        <strong>Last updated:</strong> May 13, 2026
      </p>

      <p>
        This SMS Policy explains how the CIVIX platform handles SMS messaging on behalf of our
        customers (independent insurance agents and agencies, "Agents") and the protections we
        provide to message recipients. CIVIX LLC is the platform provider; the Agent is the message
        sender under the Telephone Consumer Protection Act (TCPA).
      </p>

      <h2>1. Who sends the messages</h2>
      <p>
        When you receive an SMS originating from a CIVIX-provisioned phone number, the message is
        sent by an independent licensed insurance Agent who has uploaded or captured your contact
        information through their own marketing channels. The Agent has represented to CIVIX that
        you provided prior express written consent to be contacted. CIVIX provides the underlying
        infrastructure (Twilio + 10DLC) but is not the message sender.
      </p>

      <h2>2. Use cases</h2>
      <p>Messages sent through CIVIX fall into the following carrier-approved use cases:</p>
      <ul>
        <li><strong>Account notifications.</strong> Confirmations of appointments, policy applications, and document uploads.</li>
        <li><strong>Customer care.</strong> Responses to a recipient's inquiry about an insurance quote, coverage, or scheduled appointment.</li>
        <li><strong>Marketing (limited).</strong> Outreach by a licensed Agent to a lead who has expressly consented in writing to be contacted about insurance products. CIVIX does not permit affiliate marketing, lead resale, or generic promotional blasts.</li>
      </ul>
      <p>CIVIX does <strong>not</strong> permit messages relating to:</p>
      <ul>
        <li>Sex, hate, alcohol, firearms, tobacco (SHAFT)</li>
        <li>Cannabis or controlled substances</li>
        <li>Loans (payday, short-term high-interest)</li>
        <li>Cryptocurrencies, stock recommendations, or gambling</li>
        <li>Debt forgiveness, debt collection without proper licensing</li>
        <li>Multi-level marketing, lead generation aggregation, lead resale</li>
      </ul>

      <h2>3. Consent requirements</h2>
      <p>Before any message is sent, the Agent must have obtained from you:</p>
      <ul>
        <li>Your name and phone number</li>
        <li>Express written consent to receive marketing or informational SMS messages about insurance products from the specific Agent</li>
        <li>A timestamp and source URL (or paper-form reference) documenting consent</li>
        <li>Disclosure that consent is not a condition of any purchase, that message and data rates may apply, and that you can opt out at any time by texting STOP</li>
      </ul>
      <p>
        CIVIX enforces a per-lead consent gate. If the consent fields on the lead record are missing,
        no message can be sent through the platform — the dispatch is blocked at the database query
        layer.
      </p>

      <h2>4. Opt-out (STOP)</h2>
      <p>You may opt out of further SMS at any time by replying with any of the following keywords:</p>
      <ul>
        <li><strong>STOP</strong></li>
        <li><strong>END</strong></li>
        <li><strong>QUIT</strong></li>
        <li><strong>CANCEL</strong></li>
        <li><strong>UNSUBSCRIBE</strong></li>
        <li><strong>OPTOUT</strong></li>
        <li><strong>REVOKE</strong></li>
      </ul>
      <p>
        Punctuation and case do not matter ("Stop", "STOP!", "stop.", "STOP PLEASE" all work). On
        receipt of any STOP keyword, CIVIX:
      </p>
      <ul>
        <li>Marks the recipient as "do-not-contact" across the tenant within seconds</li>
        <li>Suppresses all future SMS and outbound calls to that number from any agent in the tenant</li>
        <li>Sends a final confirmation: "You have been unsubscribed. You will receive no further messages. Reply HELP for help."</li>
      </ul>
      <p>To resume messages, reply <strong>START</strong> or <strong>UNSTOP</strong>. This is the only way to re-subscribe.</p>

      <h2>5. Help (HELP)</h2>
      <p>
        Reply <strong>HELP</strong> to receive CIVIX support contact information. The auto-response
        is: "CIVIX support: ceo@civixcrm.ai · +1 (719) 387-0657. Reply STOP to unsubscribe."
      </p>

      <h2>6. Message frequency and rates</h2>
      <p>
        Message frequency varies depending on your engagement with the Agent and the use case. Most
        recipients receive between 1 and 8 messages per month. <strong>Message and data rates may
        apply.</strong> Check with your carrier for details.
      </p>

      <h2>7. Quiet hours</h2>
      <p>
        CIVIX does not transmit any SMS or outbound call to a number between 9:00 p.m. and 8:00 a.m.
        in the recipient's local time zone, regardless of what the Agent attempts to send.
        Quiet-hours blocking is enforced server-side and cannot be overridden by the Agent.
      </p>

      <h2>8. Do-Not-Call (DNC) protections</h2>
      <p>Before any outbound dial or SMS, CIVIX scrubs the recipient's number against:</p>
      <ul>
        <li>The Federal Do Not Call Registry (refreshed daily)</li>
        <li>The tenant's internal DNC list (populated automatically by STOP replies, complaints, and manual entries)</li>
        <li>Any state DNC list applicable to the recipient's state, where CIVIX supports it</li>
      </ul>
      <p>If any of these checks fail, the message or call is blocked.</p>

      <h2>9. Licensing check</h2>
      <p>
        For insurance-related messaging, CIVIX blocks any message or call when the Agent does not
        hold an active license in the recipient's state. This protects you from receiving
        solicitations from unlicensed sources.
      </p>

      <h2>10. Carrier policies</h2>
      <p>
        CIVIX uses Twilio as its messaging carrier aggregator. Messages are subject to The Campaign
        Registry (TCR) and CTIA Short Code Monitoring Handbook policies, plus Twilio's Acceptable Use
        Policy. Repeated violations result in carrier-level suspension of the offending Agent's
        number and tenant.
      </p>

      <h2>11. Reporting abuse</h2>
      <p>
        If you believe a message you received violates this Policy or applicable law, please email{' '}
        <Mail /> with:
      </p>
      <ul>
        <li>The phone number that contacted you</li>
        <li>Date and time of the message</li>
        <li>A copy of the message text</li>
        <li>Whether you previously gave consent</li>
      </ul>
      <p>We investigate every complaint within 1 business day. Confirmed violations result in tenant suspension or termination.</p>

      <h2>12. Contact</h2>
      <p>
        CIVIX LLC<br />
        5600 S Quebec St Suite 300<br />
        Greenwood Village, CO 80111<br />
        Email: <Mail /><br />
        Phone: +1 (719) 387-0657
      </p>

      <p>
        See also: <Link to="/privacy">Privacy Policy</Link> · <Link to="/terms">Terms of Service</Link>
      </p>
    </LegalLayout>
  );
}
